import { environment } from "@environments/environment";
import { v4 as uuidv4 } from 'uuid';

// Access the Web Crypto API available in the browser
const { subtle } = window.crypto;

export default class HelpersUtils {
  private static readonly IDB_DB_NAME = environment.IDB_DB_NAME;
  private static readonly IDB_STORE_NAME = environment.IDB_STORE_NAME;
  private static readonly SESSION_KEY_ID = environment.SESSION_KEY_ID;

  /**
   * Open IndexedDB database and create object store if it doesn't exist
   * Returns a Promise<IDBDatabase>.
   */
  private static openDBRequest(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const _req = indexedDB.open(this.IDB_DB_NAME, 1);

      _req.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        db.createObjectStore(this.IDB_STORE_NAME);
      }

      _req.onsuccess = (event: Event) => resolve((event.target as IDBOpenDBRequest).result);
      _req.onerror = (event: Event) => reject(event);
    });
  }

  /**
   * read a CryptoKeyPair from IndexedDB by id.
   * Returns Promise<CryptoKeyPair | null>.
   * @param db 
   * @param id 
   */
  private static idbGet(db: IDBDatabase, id: string): Promise<CryptoKeyPair | null> {
    return new Promise((resolve, reject) => {
      const req = db
        .transaction(this.IDB_STORE_NAME, 'readonly')
        .objectStore(this.IDB_STORE_NAME)
        .get(id);
      req.onsuccess = (e: Event) => resolve((e.target as IDBRequest).result ?? null);
      req.onerror = (e: Event) => reject((e.target as IDBRequest).error);
    });
  }

  /**
 * write a CryptoKeyPair into IndexedDB.
 * CryptoKey objects are structured-cloneable even when non-extractable,
 * so the raw key material is kept in the browser's secure key store.
 */
  private static idbPut(db: IDBDatabase, id: string, value: CryptoKeyPair): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = db
        .transaction(this.IDB_STORE_NAME, 'readwrite')
        .objectStore(this.IDB_STORE_NAME)
        .put(value, id);
      req.onsuccess = () => resolve(undefined);
      req.onerror = (e: Event) => reject((e.target as IDBRequest).error);
    });
  }

  // Base64url encoding for ArrayBuffers and JSON objects
  private static b64url(buf: ArrayBuffer | Uint8Array): string {
    const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/=+$/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  // Convenience method for JSON objects to be encoded as base64url strings for JWT headers and payloads
  private static jsonB64url(obj: any): string {
    const json = JSON.stringify(obj);
    const encoder = new TextEncoder();
    return this.b64url(encoder.encode(json));
  }

  /**
   * Generate a new ECDSA P-256 key pair for signing and verifying.
   * @returns Promise<CryptoKeyPair> - a newly generated ECDSA P-256 key pair, non-extractable, for signing and verifying.
   */
  private static async generateKeyPair(): Promise<CryptoKeyPair> {
    return subtle.generateKey(
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,          // non-extractable: raw bytes never accessible to JS
      ['sign', 'verify']
    );
  }

  /**
   * Returns the session CryptoKeyPair, creating and persisting it on first call.
   * Browser flow:
   * 1. Open IndexedDB.
   * 2. Look up SESSION_KEY_ID.
   * 3. If found → return cached pair (same key for every token in this session).
   * 4. If not found → generate fresh pair, store it, return it.
   * 
   * @returns Promise<CryptoKeyPair> - the session CryptoKeyPair.
   */
  private static async getOrCreateSessionKeyPair(): Promise<CryptoKeyPair> {
    if (typeof indexedDB !== 'undefined') {
      const db = await this.openDBRequest();
      let keyPair = await this.idbGet(db, this.SESSION_KEY_ID);
      if (!keyPair) {
        keyPair = await this.generateKeyPair();
        await this.idbPut(db, this.SESSION_KEY_ID, keyPair);
      }
      return keyPair;
    }
    // Fallback: generate a key pair without persisting (SSR or no IndexedDB)
    return await this.generateKeyPair();
  }

  /**
   *  Generate a DPoP token bound to the given HTTP method and URL, signed with the session key pair.
   *  Returns an object containing the token string, the Redis key (SHA-256 hash of jti), and the jti itself.
   *  @param keyPair - the CryptoKeyPair to use for signing (should be the session key pair).
   *  @param htu - the HTTP URL (htu) that this token is bound to, e.g. "https://api.example.com/resource".
   *  @param htm - the HTTP method (htm) that this token is bound to, e.g. "POST". Defaults to "POST".
   *  @returns Promise<{ token: string, redisKey: string, jti: string }> - the generated DPoP token, the Redis key for server-side storage, and the jti for reference.
   */
  static async generateDPoPToken(keyPair: any, htu: string, htm: string = 'POST'): Promise<{ identifer: string, cache: string, jti: string }> {
    // Reuses the session key pair — generated once, stored in IndexedDB.
    const { publicKey, privateKey } = await this.getOrCreateSessionKeyPair();

    // Export public key as JWK to embed in the header
    const publicKeyJwk = await subtle.exportKey('jwk', publicKey);

    // Strip private-key fields just in case (d, dp, dq, p, q, qi)
    const { d, dp, dq, p, q, qi, ...pubJwk } = publicKeyJwk;

    const jti = uuidv4();

    const header = {
      alg: 'ES256',
      typ: 'dpop+jwt',
      jwk: pubJwk           // ← public key travels with the token; NO secret needed
    };

    const payload = {
      jti,
      keyid: keyPair,
      token_use: 'id',
      iat: Math.floor(Date.now() / 1000),
      htu,                  // e.g. "https://api.example.com/resource"
      htm                   // e.g. "POST"
    };

    const signingInput = `${this.jsonB64url(header)}.${this.jsonB64url(payload)}`;

    const encoder = new TextEncoder();
    const signature = await subtle.sign(
      { name: 'ECDSA', hash: 'SHA-256' },
      privateKey,           // ← private key stays here, never sent
      encoder.encode(signingInput)
    );

    const identifer = `${signingInput}.${this.b64url(signature)}`;

    // Redis key: SHA-256(jti) — no hardcoded suffix needed
    // The jti alone is already a UUID (128-bit random); hashing it just adds uniformity.
    const encoder2 = new TextEncoder();
    const jtiHash = await subtle.digest('SHA-256', encoder2.encode(jti));
    const cache = this.b64url(jtiHash);

    return { identifer, cache, jti };
  }
}